import React, { useState, useEffect } from 'react';

/**
 * Animated BBA brand background used behind the login and signup cards.
 *
 * The hero slot cycles through three scenes — a rocket assembling and launching,
 * a road being paved for a bus to drive, and a rising tide lifting a fleet of
 * boats. Pass `hero` to pin a single scene instead of cycling.
 */

// index order matches sceneRocket / sceneRoad / sceneTide
const SCENE_DURATIONS = [16000, 12000, 18000];
const HERO_TO_SCENE = { rocket: 0, road: 1, tide: 2 };

const prefersReducedMotion = () =>
  typeof window !== 'undefined' &&
  typeof window.matchMedia === 'function' &&
  window.matchMedia('(prefers-reduced-motion: reduce)').matches;

const LoginBackground = ({ hero = 'cycle', showGrid = true, showMetrics = true }) => {
  const [scene, setScene] = useState(0);

  useEffect(() => {
    if (hero !== 'cycle' || prefersReducedMotion()) return undefined;
    const timer = setTimeout(
      () => setScene((s) => (s + 1) % SCENE_DURATIONS.length),
      SCENE_DURATIONS[scene] ?? SCENE_DURATIONS[0]
    );
    return () => clearTimeout(timer);
  }, [hero, scene]);

  const activeScene = hero === 'cycle' ? scene : HERO_TO_SCENE[hero] ?? 0;

  return (
    <div className="bba-lb">
      <style>{`
      .bba-lb {
        position: fixed;
        inset: 0;
        overflow: hidden;
        background: radial-gradient(120% 90% at 70% 30%, #221d19 0%, #14110f 62%, #0d0b09 100%);
      }

      @keyframes bbaSpin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
      @keyframes bbaGrow {
        0%, 12% { transform: scaleY(0.12); }
        55%, 78% { transform: scaleY(1); }
        100% { transform: scaleY(0.12); }
      }
      @keyframes bbaDraw { from { stroke-dashoffset: 900; } to { stroke-dashoffset: 0; } }
      @keyframes bbaPulse { 0%, 100% { opacity: 0.05; } 50% { opacity: 0.09; } }
      @keyframes bbaDrift {
        0% { transform: translate(0, 0) rotate(0deg) scale(1); }
        50% { transform: translate(2vw, -4vh) rotate(14deg) scale(1.06); }
        100% { transform: translate(0, 0) rotate(0deg) scale(1); }
      }
      @keyframes bbaGridSlide { from { background-position: 0 0; } to { background-position: 0 -64px; } }

      /* --- rocket scene --- */
      @keyframes bbaPartNose {
        0% { transform: translate(-130px, -160px) rotate(-35deg); opacity: 0; }
        6% { opacity: 1; }
        20%, 100% { transform: translate(0, 0) rotate(0deg); opacity: 1; }
      }
      @keyframes bbaPartBody {
        0% { transform: translate(150px, 80px) rotate(20deg); opacity: 0; }
        5% { opacity: 1; }
        16%, 100% { transform: translate(0, 0) rotate(0deg); opacity: 1; }
      }
      @keyframes bbaPartFinL {
        0%, 14% { transform: translate(-140px, 120px) rotate(-50deg); opacity: 0; }
        18% { opacity: 1; }
        27%, 100% { transform: translate(0, 0) rotate(0deg); opacity: 1; }
      }
      @keyframes bbaPartFinR {
        0%, 16% { transform: translate(140px, 130px) rotate(50deg); opacity: 0; }
        20% { opacity: 1; }
        29%, 100% { transform: translate(0, 0) rotate(0deg); opacity: 1; }
      }
      @keyframes bbaPartWin {
        0%, 28% { transform: scale(0); opacity: 0; }
        33% { transform: scale(1.25); opacity: 1; }
        36%, 100% { transform: scale(1); opacity: 1; }
      }
      @keyframes bbaIgnite {
        0%, 40% { opacity: 0; transform: scaleY(0.15); }
        44% { opacity: 1; transform: scaleY(0.7); }
        47% { transform: scaleY(1.15); }
        50% { transform: scaleY(0.85); }
        54%, 100% { opacity: 1; transform: scaleY(1); }
      }
      @keyframes bbaLaunch {
        0%, 40% { transform: translate(0, 0); }
        41% { transform: translate(-2px, 1px); }
        42% { transform: translate(3px, -1px); }
        43% { transform: translate(-3px, 1px); }
        44% { transform: translate(3px, 0); }
        45% { transform: translate(-2px, -2px); }
        46% { transform: translate(3px, -4px); }
        47% { transform: translate(-2px, -8px); }
        48% { transform: translate(2px, -14px); }
        50% { transform: translate(1px, -14vh); }
        56% { transform: translate(0, -70vh); }
        62%, 100% { transform: translate(0, -150vh); }
      }
      @keyframes bbaSmokeL {
        0%, 41% { opacity: 0; transform: translate(0, 0) scale(0.15); }
        46% { opacity: 0.5; transform: translate(-30px, -6px) scale(0.9); }
        58% { opacity: 0.28; transform: translate(-80px, -26px) scale(1.9); }
        70%, 100% { opacity: 0; transform: translate(-110px, -40px) scale(2.3); }
      }
      @keyframes bbaSmokeR {
        0%, 42% { opacity: 0; transform: translate(0, 0) scale(0.15); }
        47% { opacity: 0.5; transform: translate(30px, -6px) scale(0.9); }
        59% { opacity: 0.28; transform: translate(80px, -26px) scale(1.9); }
        71%, 100% { opacity: 0; transform: translate(110px, -40px) scale(2.3); }
      }
      @keyframes bbaSmokeUp {
        0%, 43% { opacity: 0; transform: translate(0, 0) scale(0.2); }
        48% { opacity: 0.4; transform: translate(0, -30px) scale(1); }
        60% { opacity: 0.2; transform: translate(8px, -90px) scale(1.8); }
        72%, 100% { opacity: 0; transform: translate(12px, -130px) scale(2.1); }
      }
      @keyframes bbaTrailUp {
        0%, 46% { opacity: 0; transform: scaleY(0); }
        52% { opacity: 0.55; transform: scaleY(0.45); }
        60% { opacity: 0.35; transform: scaleY(1); }
        72%, 100% { opacity: 0; transform: scaleY(1); }
      }

      /* --- roadmap scene --- */
      @keyframes bbaRoadReveal {
        0% { clip-path: inset(0 0 0 100%); }
        24% { clip-path: inset(0 0 0 29%); }
        26% { clip-path: inset(0 12% 0 23%); }
        34% { clip-path: inset(0 32% 0 0); }
        50% { clip-path: inset(0 71% 0 0); }
        70%, 100% { clip-path: inset(0 100% 0 0); }
      }
      @keyframes bbaPaver {
        0% { left: 91%; opacity: 0; }
        2% { opacity: 1; }
        34% { left: 3%; }
        42%, 100% { left: -10%; opacity: 1; }
      }
      @keyframes bbaBusDrive {
        0%, 10% { transform: translateX(106vw); }
        64%, 100% { transform: translateX(-16vw); }
      }
      @keyframes bbaBusBob {
        0%, 100% { transform: translateY(0); }
        50% { transform: translateY(-2px); }
      }

      /* --- rising tide scene --- */
      @keyframes bbaTideRise {
        0% { transform: translateY(0); }
        14% { transform: translateY(-24vh); }
        48% { transform: translateY(-28vh); }
        88%, 100% { transform: translateY(-88vh); }
      }
      @keyframes bbaBoatInR {
        0%, 15% { transform: translateX(50vw); }
        32%, 100% { transform: translateX(0); }
      }
      @keyframes bbaBoatInL {
        0%, 19% { transform: translateX(-50vw); }
        37%, 100% { transform: translateX(0); }
      }
      @keyframes bbaBoatInR2 {
        0%, 23% { transform: translateX(60vw); }
        42%, 100% { transform: translateX(0); }
      }
      @keyframes bbaBoatInL2 {
        0%, 28% { transform: translateX(-60vw); }
        47%, 100% { transform: translateX(0); }
      }
      @keyframes bbaBob {
        0%, 100% { transform: translateY(0) rotate(-1.5deg); }
        50% { transform: translateY(-5px) rotate(1.5deg); }
      }
      @keyframes bbaWave { from { background-position-x: 0; } to { background-position-x: 80px; } }

      /* --- shared --- */
      @keyframes bbaSceneFade {
        0%, 86% { opacity: 1; }
        98%, 100% { opacity: 0; }
      }
      @keyframes bbaSweep {
        0% { transform: translateX(-30vw) rotate(-24deg); opacity: 0; }
        15% { opacity: 0.5; }
        85% { opacity: 0.5; }
        100% { transform: translateX(130vw) rotate(-24deg); opacity: 0; }
      }

      .bba-lb__grid {
        position: absolute;
        inset: -64px 0 0 0;
        opacity: 0.5;
        background-image:
          linear-gradient(rgba(255,255,255,0.035) 1px, transparent 1px),
          linear-gradient(90deg, rgba(255,255,255,0.035) 1px, transparent 1px);
        background-size: 64px 64px;
        animation: bbaGridSlide 14s linear infinite;
      }

      .bba-lb__emblem-white {
        position: absolute;
        top: 50%;
        left: 62%;
        width: 920px;
        height: 920px;
        margin: -460px 0 0 -460px;
        background: url('/bba-emblem.png') center / contain no-repeat;
        /* the emblem ships in brand red; flatten it to white for the watermark */
        filter: brightness(0) invert(1);
        animation: bbaSpin 240s linear infinite, bbaPulse 9s ease-in-out infinite;
      }

      .bba-lb__emblem-red {
        position: absolute;
        bottom: -140px;
        left: -120px;
        width: 460px;
        height: 460px;
        opacity: 0.10;
        background: url('/bba-emblem.png') center / contain no-repeat;
        animation: bbaDrift 22s ease-in-out infinite;
      }

      .bba-lb__line-left { position: absolute; left: 3vw; bottom: 8vh; width: 30vw; height: 56vh; opacity: 0.55; }
      .bba-lb__line-right { position: absolute; right: 2vw; top: 6vh; width: 26vw; height: 34vh; opacity: 0.35; }
      .bba-lb__svg { width: 100%; height: 100%; }
      .bba-lb__draw { animation: bbaDraw 7s cubic-bezier(0.2,0,0.2,1) infinite alternate; }
      .bba-lb__draw--glow { animation: bbaDraw 7s cubic-bezier(0.2,0,0.2,1) infinite alternate; filter: blur(6px); }
      .bba-lb__draw--slow { animation: bbaDraw 9s cubic-bezier(0.2,0,0.2,1) -3s infinite alternate; }

      .bba-lb__bars-top {
        position: absolute; left: 5vw; top: 0;
        display: flex; align-items: flex-start; gap: 12px;
        height: 22vh; opacity: 0.35; transform: rotate(180deg);
      }
      .bba-lb__bars-right {
        position: absolute; right: 6vw; bottom: 0;
        display: flex; align-items: flex-end; gap: 18px;
        height: 44vh; opacity: 0.5;
      }
      .bba-lb__bar { transform-origin: bottom; border-radius: 3px 3px 0 0; }
      .bba-lb__bar--accent {
        background: linear-gradient(to top, rgba(248,7,7,0.55), rgba(248,7,7,0.9));
        box-shadow: 0 0 24px rgba(248,7,7,0.35);
      }

      .bba-lb__rocket { position: absolute; left: 22%; bottom: 5vh; width: 130px; height: 260px; }
      .bba-lb__trail {
        position: absolute; left: 58px; bottom: 30px; width: 14px; height: 90vh;
        transform-origin: bottom;
        background: linear-gradient(to top, rgba(248,7,7,0.55), rgba(248,7,7,0));
        border-radius: 7px; filter: blur(3px);
        animation: bbaTrailUp 16s linear infinite;
      }
      .bba-lb__smoke { position: absolute; border-radius: 50%; }
      .bba-lb__rocket-body { position: absolute; inset: 0; animation: bbaLaunch 16s cubic-bezier(0.5, 0, 0.9, 0.4) infinite; }
      .bba-lb__rocket-svg { width: 100%; height: 100%; overflow: visible; }
      .bba-lb__flame { animation: bbaIgnite 16s linear infinite; transform-origin: 65px 212px; transform-box: view-box; }
      .bba-lb__part-body { animation: bbaPartBody 16s ease-out infinite; }
      .bba-lb__part-nose { animation: bbaPartNose 16s ease-out infinite; }
      .bba-lb__part-win { animation: bbaPartWin 16s ease-out infinite; transform-origin: 65px 118px; transform-box: view-box; }
      .bba-lb__part-fin-l { animation: bbaPartFinL 16s ease-out infinite; }
      .bba-lb__part-fin-r { animation: bbaPartFinR 16s ease-out infinite; }

      .bba-lb__road-scene {
        position: absolute; top: 8vh; left: 0; width: 100%; height: 140px;
        animation: bbaSceneFade 12s linear 1 both;
      }
      .bba-lb__road {
        position: absolute; top: 64px; left: 3%; right: 3%; height: 30px;
        border-radius: 15px; background: rgba(255,255,255,0.14);
        box-shadow: 0 2px 0 rgba(255,255,255,0.06);
        animation: bbaRoadReveal 12s linear 1 both;
      }
      .bba-lb__road-dashes {
        position: absolute; left: 16px; right: 16px; top: 13px; height: 4px; border-radius: 2px;
        background: repeating-linear-gradient(90deg, #F80707 0 26px, transparent 26px 54px);
      }
      .bba-lb__paver { position: absolute; top: 30px; width: 64px; height: 60px; animation: bbaPaver 12s linear 1 both; }
      .bba-lb__bus { position: absolute; top: 14px; left: 0; width: 130px; height: 58px; animation: bbaBusDrive 12s linear 1 both; }
      .bba-lb__bus-bob { width: 100%; height: 100%; animation: bbaBusBob 0.5s ease-in-out infinite; }
      .bba-lb__bus-svg { width: 100%; height: 100%; transform: scaleX(-1); }

      .bba-lb__tide-scene { position: absolute; inset: 0; overflow: hidden; animation: bbaSceneFade 15s linear 1 both; }
      .bba-lb__tide {
        position: absolute; left: 0; top: 100%; width: 100%; height: 130vh;
        animation: bbaTideRise 15s ease-in-out 1 both;
        background: linear-gradient(to bottom, rgba(72,108,142,0.55) 0%, rgba(42,64,88,0.6) 30%, rgba(20,32,46,0.75) 100%);
      }
      .bba-lb__wave-back {
        position: absolute; top: -10px; left: 0; width: 100%; height: 14px;
        background: radial-gradient(14px 10px at 20px 14px, rgba(255,255,255,0.28) 0 9px, transparent 10px);
        background-size: 40px 14px; background-repeat: repeat-x;
        animation: bbaWave 5s linear infinite;
      }
      .bba-lb__wave-front {
        position: absolute; top: -4px; left: 0; width: 100%; height: 12px;
        background: radial-gradient(12px 9px at 16px 12px, rgba(255,255,255,0.18) 0 8px, transparent 9px);
        background-size: 32px 12px; background-repeat: repeat-x;
        animation: bbaWave 3.4s linear infinite reverse;
      }
      .bba-lb__boat-inner { width: 100%; height: 100%; }

      .bba-lb__sweep {
        position: absolute; top: -20vh; left: 0; width: 18vw; height: 140vh;
        background: linear-gradient(90deg, rgba(248,7,7,0) 0%, rgba(248,7,7,0.05) 50%, rgba(248,7,7,0) 100%);
        animation: bbaSweep 16s ease-in-out infinite;
      }

      .bba-lb__vignette {
        position: absolute; inset: 0; pointer-events: none;
        background: radial-gradient(70% 70% at 50% 50%, rgba(13,11,9,0) 40%, rgba(13,11,9,0.5) 100%);
      }

      @media (prefers-reduced-motion: reduce) {
        .bba-lb *, .bba-lb *::before, .bba-lb *::after { animation: none !important; }
      }
    `}</style>

      {showGrid && <div className="bba-lb__grid" />}

      <div className="bba-lb__emblem-white" />
      <div className="bba-lb__emblem-red" />

      {showMetrics && (
        <>
          {/* ascending profit line, lower left */}
          <div className="bba-lb__line-left">
            <svg viewBox="0 0 800 500" className="bba-lb__svg" preserveAspectRatio="none">
              <path
                d="M 10 470 L 130 420 L 240 445 L 370 330 L 480 360 L 610 190 L 790 60"
                fill="none" stroke="rgba(248,7,7,0.22)" strokeWidth="14" strokeLinecap="round"
                strokeLinejoin="round" strokeDasharray="900" className="bba-lb__draw--glow"
              />
              <path
                d="M 10 470 L 130 420 L 240 445 L 370 330 L 480 360 L 610 190 L 790 60"
                fill="none" stroke="#F80707" strokeWidth="4" strokeLinecap="round"
                strokeLinejoin="round" strokeDasharray="900" className="bba-lb__draw"
              />
            </svg>
          </div>

          {/* second ascending line, upper right */}
          <div className="bba-lb__line-right">
            <svg viewBox="0 0 800 500" className="bba-lb__svg" preserveAspectRatio="none">
              <path
                d="M 10 480 L 150 400 L 260 430 L 420 280 L 540 320 L 680 140 L 790 40"
                fill="none" stroke="rgba(255,255,255,0.7)" strokeWidth="4" strokeLinecap="round"
                strokeLinejoin="round" strokeDasharray="900" className="bba-lb__draw--slow"
              />
            </svg>
          </div>

          {/* small bar cluster hanging from the top left */}
          <div className="bba-lb__bars-top">
            <div className="bba-lb__bar" style={{ width: 16, height: '40%', background: 'rgba(255,255,255,0.12)', animation: 'bbaGrow 9s ease-in-out -1s infinite' }} />
            <div className="bba-lb__bar" style={{ width: 16, height: '62%', background: 'rgba(255,255,255,0.14)', animation: 'bbaGrow 9s ease-in-out -0.4s infinite' }} />
            <div className="bba-lb__bar" style={{ width: 16, height: '52%', background: 'rgba(255,255,255,0.12)', animation: 'bbaGrow 9s ease-in-out 0.4s infinite' }} />
            <div className="bba-lb__bar" style={{ width: 16, height: '84%', background: 'linear-gradient(to top, rgba(248,7,7,0.5), rgba(248,7,7,0.8))', animation: 'bbaGrow 9s ease-in-out 1s infinite' }} />
          </div>

          {/* rising bar cluster, right side */}
          <div className="bba-lb__bars-right">
            <div className="bba-lb__bar" style={{ width: 26, height: '34%', background: 'rgba(255,255,255,0.10)', animation: 'bbaGrow 8s ease-in-out infinite' }} />
            <div className="bba-lb__bar" style={{ width: 26, height: '48%', background: 'rgba(255,255,255,0.12)', animation: 'bbaGrow 8s ease-in-out 0.5s infinite' }} />
            <div className="bba-lb__bar" style={{ width: 26, height: '42%', background: 'rgba(255,255,255,0.10)', animation: 'bbaGrow 8s ease-in-out 1s infinite' }} />
            <div className="bba-lb__bar" style={{ width: 26, height: '66%', background: 'rgba(255,255,255,0.14)', animation: 'bbaGrow 8s ease-in-out 1.5s infinite' }} />
            <div className="bba-lb__bar" style={{ width: 26, height: '82%', background: 'rgba(255,255,255,0.16)', animation: 'bbaGrow 8s ease-in-out 2s infinite' }} />
            <div className="bba-lb__bar bba-lb__bar--accent" style={{ width: 26, height: '100%', animation: 'bbaGrow 8s ease-in-out 2.5s infinite' }} />
          </div>
        </>
      )}

      {/* hero scene 0 — rocket assembly + launch */}
      {activeScene === 0 && (
        <div className="bba-lb__rocket">
          <div className="bba-lb__trail" />
          <div className="bba-lb__smoke" style={{ left: 25, bottom: 6, width: 80, height: 46, background: 'rgba(190,182,174,0.5)', filter: 'blur(10px)', animation: 'bbaSmokeL 16s ease-out infinite' }} />
          <div className="bba-lb__smoke" style={{ left: 30, bottom: 4, width: 76, height: 44, background: 'rgba(205,198,190,0.45)', filter: 'blur(10px)', animation: 'bbaSmokeR 16s ease-out infinite' }} />
          <div className="bba-lb__smoke" style={{ left: 12, bottom: 0, width: 56, height: 34, background: 'rgba(170,162,154,0.4)', filter: 'blur(9px)', animation: 'bbaSmokeL 16s ease-out 0.4s infinite' }} />
          <div className="bba-lb__smoke" style={{ left: 62, bottom: 0, width: 56, height: 34, background: 'rgba(170,162,154,0.4)', filter: 'blur(9px)', animation: 'bbaSmokeR 16s ease-out 0.4s infinite' }} />
          <div className="bba-lb__smoke" style={{ left: 42, bottom: 10, width: 48, height: 48, background: 'rgba(200,192,184,0.35)', filter: 'blur(11px)', animation: 'bbaSmokeUp 16s ease-out infinite' }} />

          <div className="bba-lb__rocket-body">
            <svg viewBox="0 0 130 260" className="bba-lb__rocket-svg">
              <g className="bba-lb__flame">
                <path d="M65 212 C78 224 80 238 65 256 C50 238 52 224 65 212 Z" fill="#F80707" opacity="0.85" />
                <path d="M65 216 C72 225 73 234 65 245 C57 234 58 225 65 216 Z" fill="#FFD9A8" />
                <path d="M65 220 C69 226 69 232 65 238 C61 232 61 226 65 220 Z" fill="#FFFFFF" />
              </g>
              <g className="bba-lb__part-body">
                <path d="M43 84 L87 84 L87 182 C87 190 78 196 65 196 C52 196 43 190 43 182 Z" fill="#F3EFE9" stroke="rgba(20,17,15,0.35)" strokeWidth="2" />
                <rect x="43" y="158" width="44" height="8" fill="rgba(248,7,7,0.85)" />
                <path d="M54 196 L76 196 L72 210 L58 210 Z" fill="#4a423b" />
              </g>
              <g className="bba-lb__part-nose">
                <path d="M65 14 C81 34 87 58 87 84 L43 84 C43 58 49 34 65 14 Z" fill="#F80707" />
                <path d="M65 22 C57 34 52 48 50 62 L56 62 C58 50 61 38 67 26 Z" fill="rgba(255,255,255,0.35)" />
              </g>
              <g className="bba-lb__part-win">
                <circle cx="65" cy="118" r="17" fill="#14110f" stroke="#F80707" strokeWidth="4" />
                <circle cx="65" cy="118" r="10" fill="#2b241f" />
                <circle cx="61" cy="113" r="3.5" fill="rgba(255,255,255,0.55)" />
              </g>
              <g className="bba-lb__part-fin-l">
                <path d="M43 138 L16 186 L16 202 L43 178 Z" fill="#C90505" />
              </g>
              <g className="bba-lb__part-fin-r">
                <path d="M87 138 L114 186 L114 202 L87 178 Z" fill="#C90505" />
              </g>
            </svg>
          </div>
        </div>
      )}

      {/* hero scene 1 — a road is paved right-to-left, then a bus drives it */}
      {activeScene === 1 && (
        <div className="bba-lb__road-scene">
          <div className="bba-lb__road">
            <div className="bba-lb__road-dashes" />
          </div>

          <div className="bba-lb__paver">
            <svg viewBox="0 0 64 60" className="bba-lb__svg">
              <rect x="6" y="14" width="40" height="22" rx="4" fill="#F80707" />
              <rect x="38" y="4" width="18" height="20" rx="3" fill="#C90505" />
              <rect x="42" y="8" width="10" height="8" rx="2" fill="rgba(255,255,255,0.7)" />
              <circle cx="16" cy="44" r="9" fill="#2b241f" stroke="rgba(255,255,255,0.4)" strokeWidth="2" />
              <circle cx="46" cy="44" r="9" fill="#2b241f" stroke="rgba(255,255,255,0.4)" strokeWidth="2" />
              <circle cx="16" cy="44" r="3" fill="rgba(255,255,255,0.5)" />
              <circle cx="46" cy="44" r="3" fill="rgba(255,255,255,0.5)" />
            </svg>
          </div>

          <div className="bba-lb__bus">
            <div className="bba-lb__bus-bob">
              <svg viewBox="0 0 130 58" className="bba-lb__bus-svg">
                <rect x="4" y="4" width="122" height="38" rx="8" fill="#F3EFE9" />
                <rect x="4" y="26" width="122" height="8" fill="#F80707" />
                <rect x="12" y="10" width="18" height="13" rx="2" fill="#2b241f" />
                <rect x="36" y="10" width="18" height="13" rx="2" fill="#2b241f" />
                <rect x="60" y="10" width="18" height="13" rx="2" fill="#2b241f" />
                <rect x="84" y="10" width="18" height="13" rx="2" fill="#2b241f" />
                <path d="M108 10 L122 10 Q126 10 126 14 L126 23 L108 23 Z" fill="#2b241f" />
                <circle cx="32" cy="46" r="9" fill="#14110f" stroke="rgba(255,255,255,0.45)" strokeWidth="2.5" />
                <circle cx="98" cy="46" r="9" fill="#14110f" stroke="rgba(255,255,255,0.45)" strokeWidth="2.5" />
                <circle cx="32" cy="46" r="3" fill="rgba(255,255,255,0.5)" />
                <circle cx="98" cy="46" r="3" fill="rgba(255,255,255,0.5)" />
              </svg>
            </div>
          </div>
        </div>
      )}

      {/* hero scene 2 — the tide comes in and lifts every boat with it */}
      {activeScene === 2 && (
        <div className="bba-lb__tide-scene">
          <div className="bba-lb__tide">
            <div className="bba-lb__wave-back" />
            <div className="bba-lb__wave-front" />

            {/* ferry, entering from the right */}
            <div style={{ position: 'absolute', top: -66, right: '7%', width: 190, height: 76, animation: 'bbaBoatInR 15s ease-out 1 both' }}>
              <div className="bba-lb__boat-inner" style={{ animation: 'bbaBob 6s ease-in-out infinite' }}>
                <svg viewBox="0 0 190 76" className="bba-lb__svg">
                  <path d="M8 48 L182 48 L164 72 L30 72 Z" fill="#F3EFE9" />
                  <path d="M8 48 L182 48 L178 54 L14 54 Z" fill="#F80707" />
                  <rect x="52" y="30" width="76" height="18" rx="5" fill="#E7E1D8" />
                  <rect x="66" y="14" width="42" height="16" rx="4" fill="#F3EFE9" />
                  <rect x="58" y="35" width="10" height="7" rx="2" fill="#2b241f" />
                  <rect x="74" y="35" width="10" height="7" rx="2" fill="#2b241f" />
                  <rect x="90" y="35" width="10" height="7" rx="2" fill="#2b241f" />
                  <rect x="106" y="35" width="10" height="7" rx="2" fill="#2b241f" />
                  <rect x="72" y="18" width="30" height="8" rx="2" fill="#2b241f" />
                  <path d="M128 30 L152 30 L140 44 L128 44 Z" fill="#E7E1D8" />
                </svg>
              </div>
            </div>

            {/* sailboat, entering from the left */}
            <div style={{ position: 'absolute', top: -78, left: '9%', width: 110, height: 92, animation: 'bbaBoatInL 15s ease-out 1 both' }}>
              <div className="bba-lb__boat-inner" style={{ animation: 'bbaBob 5s ease-in-out -1.5s infinite' }}>
                <svg viewBox="0 0 110 92" className="bba-lb__svg">
                  <path d="M54 4 L54 62 L14 62 Z" fill="rgba(255,255,255,0.85)" />
                  <path d="M60 14 L60 62 L94 62 Z" fill="rgba(255,255,255,0.6)" />
                  <path d="M54 4 L70 10 L54 16 Z" fill="#F80707" />
                  <path d="M8 68 L102 68 L88 88 L22 88 Z" fill="#2b241f" stroke="rgba(255,255,255,0.35)" strokeWidth="2" />
                </svg>
              </div>
            </div>

            {/* red tug, entering from the left */}
            <div style={{ position: 'absolute', top: -40, left: '34%', width: 80, height: 52, animation: 'bbaBoatInL2 15s ease-out 1 both' }}>
              <div className="bba-lb__boat-inner" style={{ animation: 'bbaBob 4.4s ease-in-out -0.8s infinite' }}>
                <svg viewBox="0 0 80 52" className="bba-lb__svg">
                  <path d="M4 28 L76 28 L64 48 L16 48 Z" fill="#C90505" />
                  <rect x="24" y="12" width="28" height="16" rx="3" fill="#F3EFE9" />
                  <rect x="30" y="16" width="8" height="6" rx="1.5" fill="#2b241f" />
                  <rect x="40" y="4" width="5" height="10" rx="2" fill="#4a423b" />
                </svg>
              </div>
            </div>

            {/* small cruiser, entering from the right */}
            <div style={{ position: 'absolute', top: -34, right: '30%', width: 70, height: 46, animation: 'bbaBoatInR2 15s ease-out 1 both' }}>
              <div className="bba-lb__boat-inner" style={{ animation: 'bbaBob 5.6s ease-in-out -2.4s infinite' }}>
                <svg viewBox="0 0 70 46" className="bba-lb__svg">
                  <path d="M4 24 L66 24 L56 42 L14 42 Z" fill="rgba(255,255,255,0.75)" />
                  <rect x="20" y="10" width="22" height="14" rx="3" fill="#E7E1D8" />
                  <rect x="25" y="13" width="7" height="6" rx="1.5" fill="#2b241f" />
                  <rect x="44" y="16" width="12" height="4" rx="2" fill="#F80707" />
                </svg>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="bba-lb__sweep" />
      <div className="bba-lb__vignette" />
    </div>
  );
};

export default LoginBackground;
